import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, Typography, message, Spin, Alert } from 'antd';
import { LockOutlined, UserOutlined, MailOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { InviteToken, SetPasswordRequest } from '../admin/types/admin.types';
import adminApi from '../admin/services/adminApi';

const { Title, Text } = Typography;

export const SetPasswordPage: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [validating, setValidating] = useState(true);
    const [inviteData, setInviteData] = useState<InviteToken | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (token) {
            validateToken();
        } else {
            setError('Токен приглашения не найден');
            setValidating(false);
        }
    }, [token]);

    const validateToken = async () => {
        try {
            const response = await adminApi.validateInviteToken(token!);
            if (response.data.success && response.data.data) {
                setInviteData(response.data.data);
            } else {
                setError(response.data.message || 'Недействительный токен');
            }
        } catch (error: any) {
            console.error('Ошибка валидации токена:', error);
            setError('Ошибка проверки токена приглашения');
        } finally {
            setValidating(false);
        }
    };

    const handleSubmit = async (values: any) => {
        if (values.password !== values.confirmPassword) {
            message.error('Пароли не совпадают');
            return;
        }

        if (values.password.length < 6) {
            message.error('Пароль должен содержать минимум 6 символов');
            return;
        }

        setLoading(true);
        try {
            const request: SetPasswordRequest = {
                token: token!,
                password: values.password,
                confirmPassword: values.confirmPassword
            };

            const response = await adminApi.setPasswordByToken(request);
            if (response.data.success) {
                message.success('Пароль успешно установлен! Теперь вы можете войти в систему.');
                navigate('/admin');
            } else {
                message.error(response.data.message || 'Ошибка установки пароля');
            }
        } catch (error: any) {
            console.error('Ошибка установки пароля:', error);
            message.error('Ошибка установки пароля');
        } finally {
            setLoading(false);
        }
    };

    const getRoleLabel = (role: string) => {
        switch (role) {
            case 'MODERATOR': return 'Модератор';
            case 'SIGNATORY': return 'Подписант';
            default: return role;
        }
    };

    if (validating) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <Card className="w-full max-w-md">
                    <div className="text-center">
                        <Spin size="large" />
                        <Title level={4} className="mt-4">Проверка токена...</Title>
                    </div>
                </Card>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <Card className="w-full max-w-md">
                    <div className="text-center">
                        <Alert
                            message="Ошибка"
                            description={error}
                            type="error"
                            showIcon
                        />
                        <Button
                            type="primary"
                            className="mt-4"
                            onClick={() => navigate('/admin')}
                        >
                            Вернуться на главную
                        </Button>
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <Card className="w-full max-w-md">
                <div className="text-center mb-6">
                    <LockOutlined className="text-4xl text-blue-600 mb-4" />
                    <Title level={2}>Установка пароля</Title>
                    <Text type="secondary">
                        Добро пожаловать в систему "AISER"
                    </Text>
                </div>

                {inviteData && (
                    <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                        <div className="flex items-center mb-2">
                            <UserOutlined className="text-blue-600 mr-2" />
                            <Text strong>{inviteData.email}</Text>
                        </div>
                        <div className="flex items-center">
                            <MailOutlined className="text-blue-600 mr-2" />
                            <Text>Роль: {getRoleLabel(inviteData.role)}</Text>
                        </div>
                    </div>
                )}

                <Form
                    form={form}
                    onFinish={handleSubmit}
                    layout="vertical"
                    size="large"
                >
                    <Form.Item
                        name="password"
                        label="Новый пароль"
                        rules={[
                            { required: true, message: 'Введите пароль' },
                            { min: 6, message: 'Пароль должен содержать минимум 6 символов' }
                        ]}
                    >
                        <Input.Password
                            placeholder="Введите пароль"
                            prefix={<LockOutlined />}
                        />
                    </Form.Item>

                    <Form.Item
                        name="confirmPassword"
                        label="Подтверждение пароля"
                        rules={[
                            { required: true, message: 'Подтвердите пароль' },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('password') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('Пароли не совпадают'));
                                },
                            }),
                        ]}
                    >
                        <Input.Password
                            placeholder="Подтвердите пароль"
                            prefix={<LockOutlined />}
                        />
                    </Form.Item>

                    <Form.Item>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                            className="w-full"
                            size="large"
                        >
                            Установить пароль
                        </Button>
                    </Form.Item>
                </Form>

                <div className="text-center mt-4">
                    <Text type="secondary" className="text-sm">
                        После установки пароля вы сможете войти в админ-панель
                    </Text>
                </div>
            </Card>
        </div>
    );
};
